import { createContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

export const ShopContext = createContext();

const ShopContextProvider = (props) => {
  const currency = 'INR ';
  const delivery_fee = 10;
  const backendUrl = import.meta.env.VITE_BACKEND_URL;
  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState('false');
  const [cartItems, setCartItems] = useState({});
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const GUEST_CART_KEY = 'guestCartItems';
  const [products, setProducts] = useState([]);
  const [token, setToken] = useState('');
  const [wishlist, setWishlist] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pagination, setPagination] = useState({
    totalProducts: 0,
    currentPage: 1,
    totalPages: 0,
  });
  const navigate = useNavigate();

  const refreshToken = async () => {
    // Prevent multiple refresh requests
    if (isRefreshing) {
      return null; // Skip if a refresh is already in progress
    }

    setIsRefreshing(true); // Set refreshing state to true
    try {
      const response = await axios.post(
        `${backendUrl}/api/user/refresh-token`,
        {},
        { withCredentials: true }
      );

      if (response.data.success) {
        const { accessToken } = response.data;

        // Save the new access token
        setToken(accessToken);
        localStorage.setItem('token', accessToken);

        return accessToken;
      } else {
        throw new Error('Refresh token is invalid or expired');
      }
    } catch (error) {
      console.error('Error during refresh token process:', error.message);
      toast.error('Session expired. Please log in again.');
      logout(); // Log out the user if refresh fails
      return null;
    } finally {
      setIsRefreshing(false); // Reset refreshing state
    }
  };

  const fetchSearchSuggestions = async (query) => {
    try {
      const res = await axios.get(`${backendUrl}/api/product/list`, {
        params: {
          search: query,
          limit: 5,
          page: 1,
        },
      });

      if (res.data.success) {
        setSearchSuggestions(res.data.products);
      }
    } catch (err) {
      console.error('Search suggestion error:', err);
    }
  };

  // Retry Logic with Token Refresh
  const handleRequestWithRetry = async (apiCall, retryCount = 1) => {
    try {
      return await apiCall(); // Attempt the original API call
    } catch (error) {
      if (error.response?.status === 401 && retryCount > 0) {
        // Wait for any ongoing refresh process to complete
        if (isRefreshing) {
          await new Promise((resolve) => {
            const interval = setInterval(() => {
              if (!isRefreshing) {
                clearInterval(interval);
                resolve();
              }
            }, 100); // Check every 100ms
          });
        }

        // Retry with a refreshed token
        const newToken = await refreshToken();
        if (newToken) {
          return await handleRequestWithRetry(apiCall, retryCount - 1);
        }
      }
      throw error; // Rethrow the error if not recoverable
    }
  };

  // Logout Function
  const logout = () => {
    setToken(null);
    setCartItems({});
    setWishlist([]);
    localStorage.removeItem('token');
    navigate('/login');
  };

  const addToCart = async (itemId, size) => {
    if (!itemId) {
      toast.error('Invalid product ID.');
      return;
    }

    let cartData = structuredClone(cartItems);
    if (!cartData[itemId]) {
      cartData[itemId] = {};
    }
    cartData[itemId][size || 'default'] = (cartData[itemId][size || 'default'] || 0) + 1;

    setCartItems(cartData);

    if (token) {
      try {
        const response = await handleRequestWithRetry(() =>
          axios.post(
            `${backendUrl}/api/cart/add`,
            { itemId, size: size || null },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        );
        if (response.data.success) {
          toast.success('Item added to cart successfully!');
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        console.error('Error adding item to cart:', error.message);
        toast.error('Failed to add item to cart.');
      }
    }
  };

  const getCartCount = () => {
    let totalCount = 0;
    for (const items in cartItems) {
      for (const item in cartItems[items]) {
        try {
          if (cartItems[items][item] > 0) {
            totalCount += cartItems[items][item];
          }
        } catch (e) {
          console.log(e);
          toast.error(error.message);
        }
      }
    }
    return totalCount;
  };

  const updateQuantity = async (itemId, size, quantity, productFromCartPage = null) => {
    let cartData = structuredClone(cartItems);

    const product = productFromCartPage || products.find((p) => p._id === itemId);
    if (!product) {
      toast.error('Product not found or not loaded yet.');
      return;
    }

    let availableQuantity = 0;

    if (product.sizes && product.sizes.length > 0) {
      const selectedSize = product.sizes.find((s) => s.size === size);
      availableQuantity = selectedSize ? selectedSize.quantity : 0;
    } else {
      availableQuantity = product.stockQuantity;
    }

    if (quantity > availableQuantity) {
      toast.error(`Only ${availableQuantity} left in stock.`);
      return;
    }

    if (quantity > 0) {
      cartData[itemId][size] = quantity;
    } else {
      delete cartData[itemId][size];
      if (Object.keys(cartData[itemId]).length === 0) {
        delete cartData[itemId];
      }
    }

    setCartItems(cartData);

    if (token) {
      try {
        const response = await axios.post(
          `${backendUrl}/api/cart/update`,
          { itemId, size, quantity },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          toast.success('Cart updated successfully!');
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        console.error('Error updating cart:', error.message);
        toast.error('Failed to update cart.');
      }
    }
  };

  useEffect(() => {
    if (!token) {
      // ✅ Only for guest users
      const storedCartItems = localStorage.getItem(GUEST_CART_KEY);
      if (storedCartItems) {
        setCartItems(JSON.parse(storedCartItems));
      }
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      // ✅ Only for guest users
      localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cartItems));
    }
  }, [cartItems, token]);

  useEffect(() => {
    const mergeGuestCartToUserCart = async () => {
      const guestCart = localStorage.getItem(GUEST_CART_KEY);
      if (!guestCart) return;

      const parsedGuestCart = JSON.parse(guestCart);
      for (const itemId in parsedGuestCart) {
        for (const size in parsedGuestCart[itemId]) {
          const quantity = parsedGuestCart[itemId][size];
          try {
            await axios.post(
              `${backendUrl}/api/cart/add`,
              { itemId, size: size !== 'default' ? size : null, quantity },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (error) {
            console.error('Error merging guest cart:', error.message);
          }
        }
      }

      localStorage.removeItem(GUEST_CART_KEY);

      getUserCart(); // Refresh user cart after merge
    };

    if (token) {
      localStorage.setItem('token', token); // ✅ Save token
      mergeGuestCartToUserCart(); // ✅ Merge guest cart
      fetchWishlist();
      getUserCart();
    }
  }, [token]);

  let cartFetchPromise = null; // A promise to manage concurrent calls to getUserCart

  const getUserCart = async () => {
    // If a cart fetch is already in progress, return the existing promise
    if (cartFetchPromise) {
      return cartFetchPromise;
    }

    // Create a new promise for the ongoing fetch
    cartFetchPromise = (async () => {
      try {
        const accessToken = localStorage.getItem('token');

        if (!accessToken) {
          console.error('No access token found in localStorage.');
          return;
        }

        // Fetch cart data from the backend
        const response = await axios.post(
          `${backendUrl}/api/cart/get`,
          {},
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (response.data.success) {
          setCartItems(response.data.cartData || {});
          return response.data.cartData;
        } else {
          console.error('Failed to fetch cart data:', response.data.message);
          toast.error('Failed to fetch cart data.');
        }
      } catch (error) {
        console.error('Error fetching user cart:', error.message);

        // Handle token expiration and refresh
        if (error.response && error.response.status === 401) {
          console.error('Access token expired. Attempting token refresh...');
          const newAccessToken = await refreshToken();

          if (newAccessToken) {
            // Retry fetching the cart after refreshing the token
            return await getUserCart();
          } else {
            console.error('Failed to refresh token. Redirecting to login.');
            navigate('/login');
          }
        } else {
          toast.error('Failed to fetch cart data. Please try again.');
        }
      } finally {
        cartFetchPromise = null; // Reset the promise after the fetch completes
      }
    })();

    return cartFetchPromise;
  };

  const getCartAmount = () => {
    let totalAmount = 0;
    for (const items in cartItems) {
      let itemInfo = products.find((product) => product._id === items);
      for (const item in cartItems[items]) {
        try {
          if (cartItems[items][item] > 0) {
            totalAmount += itemInfo.price * cartItems[items][item];
          }
        } catch (e) {}
      }
    }
    return totalAmount;
  };

  const getProductsData = async ({
    page = 1,
    limit = 60,
    category,
    subCategory,
    sortField,
    sortOrder,
  } = {}) => {
    try {
      const params = {
        page,
        limit,
        sortField: sortField || 'date',
        sortOrder: sortOrder || -1,
      };

      if (category) params.category = category;
      if (subCategory) params.subCategory = subCategory;

      const response = await axios.get(`${backendUrl}/api/product/list`, { params });

      if (response.data.success) {
        const { products, totalPages, currentPage, totalProducts } = response.data;
        setProducts(products);
        // Optionally store pagination info too
      } else {
        toast.error(response.data.message || 'Failed to fetch products.');
      }
    } catch (error) {
      console.error('Error fetching products:', error);

      if (error.response) {
        toast.error(error.response.data.message || 'Failed to fetch products.');
      } else {
        toast.error('Unable to connect to the server. Please try again.');
      }
    }
  };

  const fetchWishlist = async () => {
    if (!token) {
      toast.error('Please log in to access your wishlist.');
      navigate('/login');
      return;
    }
    try {
      const response = await handleRequestWithRetry(() =>
        axios.get(`${backendUrl}/api/user/wishlist`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      );
      if (response.data.success) {
        setWishlist(response.data.wishlist);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching wishlist:', error.message);
    }
  };

  const addToWishlist = async (productId) => {
    // ✅ Check if already in wishlist
    const isAlreadyInWishlist = wishlist.some((item) => item._id === productId);

    if (isAlreadyInWishlist) {
      toast.info('Product is already in your wishlist!');
      return;
    }

    // ✅ If not, proceed with API call
    await handleRequestWithRetry(async () => {
      const response = await axios.post(
        `${backendUrl}/api/user/wishlist/add`,
        { productId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        fetchWishlist();
      } else {
        toast.error(response.data.message || 'Failed to add to wishlist.');
      }
    });
  };

  const removeFromWishlist = async (productId) => {
    await handleRequestWithRetry(async () => {
      try {
        const response = await axios.post(
          `${backendUrl}/api/user/wishlist/remove`,
          { productId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          fetchWishlist();
        } else {
          toast.error(response.data.message || 'Failed to remove from wishlist.', {
            position: 'top-right',
            autoClose: 3000,
          });
        }
      } catch (error) {
        toast.error('An error occurred while removing the product.', {
          position: 'bottom-right',
          autoClose: 3000,
        });
        console.error(error);
      }
    });
  };

  // Fetch wishlist when the token is available
  useEffect(() => {
    if (token) {
      fetchWishlist();
      localStorage.setItem('token', token); // Ensure token is stored in localStorage
    }
  }, [token]);

  // Load products data once when the component mounts
  useEffect(() => {
    getProductsData();
  }, []);

  // Load token from localStorage and fetch cart data if token exists

  // Auto-refresh token every 19 mins
  useEffect(() => {
    const interval = setInterval(
      async () => {
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
          const refreshed = await refreshToken();
          if (!refreshed) {
            logout(); // optional fallback if refresh fails
          }
        }
      },
      19 * 60 * 1000
    ); // 19 minutes

    return () => clearInterval(interval); // Clean up on unmount
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      getUserCart(); // Fetch cart data after token is set
    }
  }, []);

  const value = {
    products,
    currency,
    delivery_fee,
    search,
    setSearch,
    showSearch,
    setShowSearch,
    cartItems,
    setCartItems,
    addToCart,
    getCartCount,
    updateQuantity,
    getCartAmount,
    navigate,
    backendUrl,
    token,
    setToken,
    fetchWishlist,
    removeFromWishlist,
    addToWishlist,
    wishlist,
    refreshToken,
    pagination,
    setWishlist,
    searchSuggestions,
    setSearchSuggestions,
    fetchSearchSuggestions,
  };
  return <ShopContext.Provider value={value}>{props.children}</ShopContext.Provider>;
};

export default ShopContextProvider;
